import { CircularProgress } from '@mui/material'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'

import DeviceInfoPage from '@/pages/Device/DeviceInfoPage'
import DurationTestSummaryPage from '@/pages/durationTest/DurationTestSummaryPage'
import DeviceInfoDetails from '@/pages/Device/Info/DeviceInfoDetails'
import DeviceReports from '@/pages/Device/Reports/Reports'
import DurationTestPage from '@/pages/durationTest/DurationTestPage'
import {
  durationTestLoader,
  startTestLoader,
  stopTestLoader,
  testResultsLoader,
} from '@/pages/durationTest/loaders'
import StartDurationTestStep from '@/pages/durationTest/startTest/StartDurationTestStep'
import DurationTestInProgressStep from '@/pages/durationTest/inProgress/DurationTestInProgressStep'
import DurationTestResultsStep from '@/pages/durationTest/testResults/DurationTestResultsStep'
import { CreateFotaJob } from '@/pages/fota/create/CreateFotaJob'
import FotaJobPage from '@/pages/fota/FotaJobPage'
import ListAllJobs from '@/pages/fota/jobsList/ListAllJobs'
import ListErrorJobs from '@/pages/fota/jobsList/ListErrorJobs'
import ListInstalledJobs from '@/pages/fota/jobsList/ListInstalledJobs'
import {
  fotaTabCountsLoader,
  errorJobsLoader,
  installedJobsLoader,
  allJobsLoader,
  updateReadyJobsLoader,
  createJobLoader,
} from '@/pages/fota/loaders'
import UpdateReadyFotaJobs from '@/pages/fota/updateReady/ReadyForUpdates'
import InServicePage from '@/pages/inService/InService'
import {
  TakeOutOfServiceLoader,
  VerifyDeviceStatusLoader,
} from '@/pages/inService/loaders'
import { DeviceTakingOutOfService } from '@/pages/inService/takeOutofService/TakeOutOfService'
import VerifyDeviceStatus from '@/pages/inService/verifyDeviceStatus/VerifyDeviceStatus'
import IntakePage from '@/pages/intake/intake'
import { intakeLoader } from '@/pages/intake/loaders'

import { ErrorBoundaryFallbackComponent as RouteErrorElement } from './components/ErrorBoundary'
import Layout from './components/Layout/Layout'
import DeviceFota from './pages/Device/Fota/Fota'
import Notfound from './pages/Notfound'
import UnauthorizedUser from './pages/Unauthorized'
import { Suspense } from 'react'
import { PcbPasswordGenerator } from '@/pages/pcbPasswordGenerator/pcbPasswordGenerator'
import CalibrationTestPage from '@/pages/calibration/CalibrationTest'
import { calibrationLoader } from '@/pages/calibration/calibrationLoader'

const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </div>
)

const router = createBrowserRouter([
  {
    path: '/v2/unauthorized',
    element: <UnauthorizedUser />,
    errorElement: <RouteErrorElement />,
    handle: {},
  },
  {
    path: '/v2/',
    element: <Layout></Layout>,
    errorElement: <RouteErrorElement />,
    handle: {
      title: 'routes.root',
    },
    children: [
      {
        index: true,
        element: <Navigate to="/v2/main/device/intake" replace={true} />,
      },
      {
        path: 'main',
        element: <Outlet />,
        errorElement: <RouteErrorElement />,
        handle: {
          title: 'routes.main',
        },
        children: [
          {
            index: true,
            path: 'device/intake',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <IntakePage />
              </Suspense>
            ),
            loader: intakeLoader,
            handle: {
              title: 'app:device.intake.pageTitle',
            },
          },
          {
            index: true,
            path: 'device/pcb-password-generator',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <PcbPasswordGenerator />
              </Suspense>
            ),
            handle: {
              title: 'app:device.pcbPasswordGenerator.pageTitle',
            },
          },
          {
            path: 'device/:uid',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <DeviceInfoPage />
              </Suspense>
            ),
            errorElement: <RouteErrorElement />,
            handle: {
              isUidTitle: true,
            },
            children: [
              {
                index: true,
                element: <Navigate to="info" replace={true} />,
              },
              {
                path: 'info',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DeviceInfoDetails />
                  </Suspense>
                ),
              },
              {
                path: 'reports',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DeviceReports />
                  </Suspense>
                ),
              },
              {
                path: 'duration-test-summary',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DurationTestSummaryPage />
                  </Suspense>
                ),
              },
              {
                path: 'fota',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DeviceFota />
                  </Suspense>
                ),
              },
            ],
          },

          {
            path: 'device/fota-management',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <FotaJobPage />
              </Suspense>
            ),
            errorElement: <RouteErrorElement />,
            loader: fotaTabCountsLoader,
            handle: {
              title: 'app:device.fotaJobs.pageTitle',
            },
            children: [
              {
                index: true,
                element: <Navigate to="create" replace={true} />,
              },
              {
                path: 'create',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CreateFotaJob />
                  </Suspense>
                ),
                loader: createJobLoader,
              },
              {
                path: 'update-ready',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <UpdateReadyFotaJobs />
                  </Suspense>
                ),
                loader: updateReadyJobsLoader,
              },
              {
                path: 'failed',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ListErrorJobs autoRefresh />
                  </Suspense>
                ),
                loader: errorJobsLoader,
              },
              {
                path: 'installed',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ListInstalledJobs autoRefresh />
                  </Suspense>
                ),
                loader: installedJobsLoader,
              },
              {
                path: 'all',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ListAllJobs autoRefresh />
                  </Suspense>
                ),
                loader: allJobsLoader,
              },
            ],
          },
          {
            path: 'device/calibration',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <CalibrationTestPage />
              </Suspense>
            ),
            loader: calibrationLoader,
            handle: {
              title: 'app:device.calibration.pageTitle',
            },
          },
          {
            path: 'device/duration-test',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <DurationTestPage />
              </Suspense>
            ),
            errorElement: <RouteErrorElement />,
            loader: durationTestLoader,
            handle: {
              title: 'app:device.durationTest.pageTitle',
            },
            children: [
              {
                index: true,
                element: <Navigate to="start" replace={true} />,
              },
              {
                path: 'start',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <StartDurationTestStep />
                  </Suspense>
                ),
                loader: startTestLoader,
              },
              {
                path: 'in-progress',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DurationTestInProgressStep />
                  </Suspense>
                ),
                loader: stopTestLoader,
              },
              {
                path: 'finish',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DurationTestResultsStep />
                  </Suspense>
                ),
                loader: testResultsLoader,
              },
            ],
          },
          {
            path: 'duration-test/:uid',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <DurationTestSummaryPage />
              </Suspense>
            ),
            errorElement: <RouteErrorElement />,
            handle: {
              isUidTitle: true,
            },
          },
          {
            path: 'device/inservice',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <InServicePage />
              </Suspense>
            ),
            errorElement: <RouteErrorElement />,
            handle: {
              title: 'app:device.inService.pageTitle',
            },
            children: [
              {
                index: true,
                element: <Navigate to="take-out-of-service" replace={true} />,
              },
              {
                path: 'take-out-of-service',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DeviceTakingOutOfService />
                  </Suspense>
                ),
                loader: TakeOutOfServiceLoader,
              },
              {
                path: 'verify-device-status',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <VerifyDeviceStatus autoRefresh={true} />
                  </Suspense>
                ),
                loader: VerifyDeviceStatusLoader,
              },
            ],
          },
        ],
      },
      {
        path: 'logout',
        element: <Navigate to="/v2/login" replace={true} />,
        handle: {
          title: 'routes.logout',
        },
      },
    ],
  },
  {
    path: '*',
    element: <Notfound />,
    errorElement: <RouteErrorElement />,
    handle: {
      title: 'routes.notFound',
    },
  },
])

export default router
